import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import type {
  PortfolioPricingApplicationInput,
  QualificationPath,
} from '@/lib/portfolioPricing';
import { SERVICE_AGREEMENT_VERSION } from '@/lib/serviceAgreement';

const REFRESH_BUFFER_MS = 5 * 60 * 1000; // Refresh 5 minutes before expiry
const MIN_REFRESH_INTERVAL_MS = 30 * 1000; // Don't refresh more than once per 30 seconds

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (
    email: string,
    password: string,
    fullName: string,
    options?: {
      source?: string | null;
      qualificationPath?: QualificationPath | null;
      portfolioPricingApplication?: PortfolioPricingApplicationInput | null;
    },
  ) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  markSessionMfaVerified: (customSession?: Session | null) => void;
  isSessionMfaVerified: () => boolean;
  clearMfaMarkers: () => void;
  refreshSession: () => Promise<Session | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuthOptional = () => useContext(AuthContext);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastRefreshRef = useRef<number>(0);

  const getMfaStorageKey = (activeSession: Session | null) => {
    if (activeSession?.access_token) {
      return `mfa_verified_${activeSession.access_token}`;
    }
    if (activeSession?.user?.id) {
      return `mfa_verified_user_${activeSession.user.id}`;
    }
    return null;
  };

  const clearMfaMarkers = () => {
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('mfa_verified_')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
  };

  const markSessionMfaVerified = (customSession?: Session | null) => {
    const activeSession = customSession ?? session;
    const tokenKey = activeSession?.access_token ? `mfa_verified_${activeSession.access_token}` : null;
    const userKey = activeSession?.user?.id ? `mfa_verified_user_${activeSession.user.id}` : null;

    if (tokenKey) sessionStorage.setItem(tokenKey, 'true');
    if (userKey) sessionStorage.setItem(userKey, 'true');
  };

  const isSessionMfaVerified = () => {
    if (!session) return false;
    const tokenKey = session?.access_token ? `mfa_verified_${session.access_token}` : null;
    const userKey = session?.user?.id ? `mfa_verified_user_${session.user.id}` : null;
    return (
      (tokenKey ? sessionStorage.getItem(tokenKey) === 'true' : false) ||
      (userKey ? sessionStorage.getItem(userKey) === 'true' : false)
    );
  };

  // Proactive session refresh to prevent expiry
  const refreshSession = useCallback(async (): Promise<Session | null> => {
    const now = Date.now();
    
    // Prevent rapid refresh attempts
    if (now - lastRefreshRef.current < MIN_REFRESH_INTERVAL_MS) {
      // Return current session from state via ref-safe getter
      const { data } = await supabase.auth.getSession();
      return data.session;
    }
    
    lastRefreshRef.current = now;

    try {
      const { data, error } = await supabase.auth.refreshSession();
      
      if (error) {
        console.error('Session refresh failed:', error.message);
        return null;
      }

      return data.session;
    } catch (err) {
      console.error('Session refresh error:', err);
      return null;
    }
  }, []);

  const scheduleRefresh = useCallback((currentSession: Session | null) => {
    // Clear existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    if (!currentSession?.expires_at) return;

    const expiresAtMs = currentSession.expires_at * 1000;
    const now = Date.now();
    const timeUntilExpiry = expiresAtMs - now;
    const refreshIn = Math.max(timeUntilExpiry - REFRESH_BUFFER_MS, 0);

    // If session is already expired or about to expire, refresh now
    if (refreshIn === 0 && timeUntilExpiry > 0) {
      refreshSession();
      return;
    }

    // Schedule refresh before expiry (only if session is still valid)
    if (timeUntilExpiry > REFRESH_BUFFER_MS) {
      refreshTimerRef.current = setTimeout(() => {
        refreshSession();
      }, refreshIn);
    }
  }, [refreshSession]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, newSession) => {
        setSession(newSession);
        setUser(newSession?.user ?? null);
        setLoading(false);
        scheduleRefresh(newSession);
      }
    );

    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      setSession(initialSession);
      setUser(initialSession?.user ?? null);
      setLoading(false);
      scheduleRefresh(initialSession);
    });

    return () => {
      subscription.unsubscribe();
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [scheduleRefresh]);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signUp = async (
    email: string,
    password: string,
    fullName: string,
    options?: {
      source?: string | null;
      qualificationPath?: QualificationPath | null;
      portfolioPricingApplication?: PortfolioPricingApplicationInput | null;
    },
  ) => {
    const redirectUrl = `${window.location.origin}/portal`;
    const signupSource = typeof options?.source === 'string' && options.source.trim().length > 0
      ? options.source.trim()
      : 'app-signup';
    const qualificationPath = options?.qualificationPath ?? null;
    const portfolioPricingApplication = options?.portfolioPricingApplication ?? null;
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
          signup_source: signupSource,
          qualification_path: qualificationPath,
          company_name: portfolioPricingApplication?.businessName || null,
          service_agreement_accepted: true,
          service_agreement_version: SERVICE_AGREEMENT_VERSION,
          portfolio_pricing_requested: Boolean(portfolioPricingApplication),
          portfolio_pricing_application: portfolioPricingApplication
            ? {
                business_name: portfolioPricingApplication.businessName,
                company_url: portfolioPricingApplication.companyUrl,
                role_account_type: portfolioPricingApplication.roleAccountType,
                approximate_portfolio_size: portfolioPricingApplication.approximatePortfolioSize,
                current_site_count: portfolioPricingApplication.currentSiteCount,
                expected_site_count: portfolioPricingApplication.expectedSiteCount,
                site_ownership_type: portfolioPricingApplication.siteOwnershipType,
                use_case_summary: portfolioPricingApplication.useCaseSummary,
                primary_market: portfolioPricingApplication.primaryMarket,
                pricing_need: portfolioPricingApplication.pricingNeed,
                expected_onboarding_timeframe: portfolioPricingApplication.expectedOnboardingTimeframe,
              }
            : null,
        },
      },
    });

    if (!error) {
      // Best-effort subscribe new customers to blog updates (ignore duplicates)
      const { error: subscriptionError } = await supabase
        .from('newsletter_subscribers')
        .insert({ email });

      if (subscriptionError && subscriptionError.code !== '23505') {
        console.error('Failed to subscribe user to blog updates', subscriptionError);
      }

      // Notify support team about new account (non-blocking)
      try {
        await supabase.functions.invoke('submit-support-request', {
          body: {
            subject: portfolioPricingApplication
              ? 'New portal account created with portfolio pricing application'
              : 'New portal account created',
            message: [
              'A new portal account was created.',
              `Name: ${fullName}`,
              `Email: ${email}`,
              `Source: ${signupSource}`,
              qualificationPath ? `Qualification path: ${qualificationPath}` : null,
              portfolioPricingApplication?.businessName
                ? `Business: ${portfolioPricingApplication.businessName}`
                : null,
              portfolioPricingApplication?.expectedSiteCount
                ? `Expected ShadowScan sites: ${portfolioPricingApplication.expectedSiteCount}`
                : null,
            ]
              .filter(Boolean)
              .join('\n'),
            userEmail: email,
          },
        });
      } catch (notifyError) {
        console.error('Failed to notify support of new signup', notifyError);
      }
    }

    return { error: error as Error | null };
  };

  const signOut = async () => {
    clearMfaMarkers();
    setUser(null);
    setSession(null);
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider 
      value={{ 
        user, 
        session, 
        loading, 
        signIn, 
        signUp, 
        signOut,
        markSessionMfaVerified,
        isSessionMfaVerified,
        clearMfaMarkers,
        refreshSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
